import React, { useState, useEffect, ReactNode, useContext } from 'react';

import { useLazyQuery } from '@vimeo/core/hooks/use-api-resource';
import { User } from '@vimeo/viewer';

import { EmbedShowcaseContext } from '../../../context/useEmbedShowcase';
import { useShowcaseSignature } from '../../../hooks/useShowcaseSignature';
import { FileDownload } from '../types';
import { ClipDownloadModal } from './ClipDownloadModal';

type ChildProps = {
  openDownloadModal: VoidFunction;
};

type Props = {
  albumId: number;
  clipId: number;
  curUser?: User;
  hashedPass: string;
  ownerId: number;
  isOwnerOrAdmin: boolean;
  children: (state: ChildProps) => ReactNode;
};

/**
 * Component manages clip download modal.
 */

export const ClipDownloadModalContainer = ({
  albumId,
  clipId,
  curUser,
  hashedPass,
  ownerId,
  isOwnerOrAdmin,
  children,
}: Props) => {
  const [isModalShowing, setIsModalShowing] = useState(false);
  const [isDefrosting, setIsDefrosting] = useState(false);
  const embedShowcaseData = useContext(EmbedShowcaseContext);
  const albumSignature = useShowcaseSignature(albumId);

  const closeDownloadModal = () => {
    setIsModalShowing(false);
  };

  const openDownloadModal = () => {
    setIsModalShowing(true);
  };

  const fetchColdSourceFile = (url?: string) => {
    if (!url) return;
    fetch(url, {
      method: 'get',
      credentials: 'include',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => response.json())
      .then((jsonResponse) => {
        setIsDefrosting(jsonResponse.is_defrosting);
      });
  };

  const [fetchDownload, { data, error: downloadError }] = useLazyQuery<{
    download: FileDownload[];
    privacy: {
      download: boolean;
    };
  }>(`/albums/${albumId}/videos/${clipId}`, {
    variables: {
      fields: ['download', 'privacy.download'],
      _hashed_pass: hashedPass || '',
      is_embed: embedShowcaseData?.isShowcaseEmbeded,
      referrer: embedShowcaseData?.referrer,
      album_signature: albumSignature || '',
      album_uri: `albums/${albumId}`,
    },
  });

  useEffect(
    function fetchDownloadOnClipId() {
      if (isModalShowing && clipId !== 0) {
        fetchDownload();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isModalShowing, clipId],
  );

  return (
    <>
      {isModalShowing && data?.download && !downloadError && (
        <ClipDownloadModal
          allowDownloads={data.privacy.download}
          clipId={clipId}
          curUser={curUser}
          downloadConfig={data.download}
          ownerId={ownerId}
          isOwnerOrAdmin={isOwnerOrAdmin}
          onClose={closeDownloadModal}
          fetchColdSourceFile={fetchColdSourceFile}
          modalOpen={isModalShowing}
          isDefrosting={isDefrosting}
        />
      )}

      {children({ openDownloadModal })}
    </>
  );
};
